import { groupBy, mapValues } from 'lodash-es'
import { type ComputedRef, computed, unref } from 'vue-demi'
import { type MaybeRef } from '@vueuse/core'
import { type GetItemPositionOptions, getItemPosition as _getItemPosition } from '../utils/get-item-position'

export type UsePartitionedItemsOptions = GetItemPositionOptions

export function usePartitionedItems<T> (
  currentItems: MaybeRef<T[]>,
  currentActivePositions: MaybeRef<string[]>,
  getPositionFromItem: (item: T) => string | undefined,
  option: UsePartitionedItemsOptions
) {
  function getItemPosition (item: T) {
    return _getItemPosition(item, currentActivePositions, getPositionFromItem, option)
  }

  const partitionedItemsMap: ComputedRef<Record<string, T[]>> = computed(() => {
    // Group activations by its position
    return groupBy(
      unref(currentItems),
      (item): string | undefined => getItemPosition(item)
    )
  })

  function getItemsByPositionRef (positions: MaybeRef<string[]>) {
    return computed(() => {
      return unref(positions ?? []).reduce<T[]>((acc, item) => {
        acc.push(...(partitionedItemsMap.value[item] ?? []))
        return acc
      }, [])
    })
  }

  function getItemsMapRefByPositions (positions: MaybeRef<string[]>): ComputedRef<Record<string, T[]>> {
    return computed(() => {
      return unref(positions ?? []).reduce<Record<string, T[]>>((acc, item) => {
        acc[item] = partitionedItemsMap.value[item] ?? []
        return acc
      }, {})
    })
  }

  /**
   * Print current partitioned activations to console.
   * Useful for debugging.
   */
  function printPartition () {
    if (partitionedItemsMap.value == null || Object.keys(partitionedItemsMap.value).length === 0) {
      console.log('No partitioned items')
      return
    }
    const wrappedRecord = mapValues(partitionedItemsMap.value, (activations) => {
      return { activations }
    })
    console.group('printPartition()')
    console.log('currentActivePositions: ', unref(currentActivePositions))
    console.log('partitionedItems:', wrappedRecord)
    console.groupEnd()
  }

  return {
    partitionedItems: partitionedItemsMap,
    getItemsByPositionRef,
    getItemsMapRefByPositions,
    printPartition,
    getItemPosition
  }
}
